import { Injectable } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signOut, authState } from '@angular/fire/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, BehaviorSubject, firstValueFrom } from 'rxjs';
import { switchMap, shareReplay, startWith, distinctUntilChanged, take, timeout, catchError } from 'rxjs/operators';
import { User as FirebaseUser, browserSessionPersistence, setPersistence } from 'firebase/auth';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SessionTimeoutDialogComponent } from '../../shared/components/session-timeout-dialog/session-timeout-dialog.component';
import { environment } from '../../../environments/environment';

export interface User {
  uid: string;
  email: string | null;
  role: 'admin' | 'user' | null;
  twoFactorEnabled: boolean;
  features: Record<string, boolean>;
  isPlatformAdmin: boolean;
}

export interface MasqueradeState {
  userId: string;
  displayName: string;
  email: string | null;
}

export interface LoginResult {
  requiresTwoFactor: boolean;
}

export interface PendingTwoFactorChallenge {
  challengeId: string;
  expiresAt: string;
  resendAvailableAt: string;
}

export type AppMode = 'platform' | 'org';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private cachedUser$ = new BehaviorSubject<User | null>(null);
  private masqueradeState$ = new BehaviorSubject<MasqueradeState | null>(null);
  user$: Observable<User | null>;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly lastActivityStorageKey = 'lastActivityAtMs';
  private readonly inactivityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
  private readonly inactivityWarningCountdownSeconds = 15;
  private readonly twoFactorSessionStorageKey = 'twoFactorSessionToken';
  private readonly pendingTwoFactorStorageKey = 'pendingTwoFactorChallenge';
  private readonly masqueradeStateStorageKey = 'masqueradeState';
  private readonly activeOrgStorageKey = 'activeOrgId';
  private readonly appModeStorageKey = 'tbdsAppMode';
  private readonly cachedUserStorageKey = 'cachedUser';
  private readonly handleActivity = () => {
    this.recordActivity();
    this.resetInactivityTimer();
  };
  private readonly handleWindowFocus = () => this.resetInactivityTimer();
  private readonly handleVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      this.resetInactivityTimer();
    }
  };
  private isTrackingActivity = false;
  private inactivityLogoutInProgress = false;
  private inactivityDialogRef: MatDialogRef<SessionTimeoutDialogComponent, 'stay' | 'logout' | 'timeout'> | null = null;
  private ignoreNextInactivityDialogClose = false;
  private sessionTimeoutToastVisible = false;

  constructor(
    private auth: Auth,
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    void this.configureAuthPersistence();

    // Load cached user from localStorage on initialization
    this.loadCachedUser();
    this.loadMasqueradeState();

    // Create user$ observable that first emits cached value, then fetches fresh profile from API
    this.user$ = authState(this.auth).pipe(
      switchMap((firebaseUser: FirebaseUser | null): Observable<User | null> => {
        if (!firebaseUser) {
          this.clearCache();
          return of(null);
        }

        // Check if we have a cached user for this UID
        const cached = this.cachedUser$.value;
        const freshData$ = this.fetchUserDataObservable(firebaseUser).pipe(
          switchMap(user => {
            if (user) {
              // Always update cache with fresh data
              this.cacheUser(user);
            }
            return of(user);
          })
        );

        if (cached && cached.uid === firebaseUser.uid) {
          // Return cached value immediately, then fresh data
          // This ensures fast initial load but also detects role changes
          return freshData$.pipe(
            startWith(cached),
            distinctUntilChanged((a, b) => 
              a?.uid === b?.uid &&
              a?.role === b?.role &&
              a?.email === b?.email &&
              a?.twoFactorEnabled === b?.twoFactorEnabled &&
              a?.isPlatformAdmin === b?.isPlatformAdmin &&
              JSON.stringify(a?.features ?? {}) === JSON.stringify(b?.features ?? {})
            )
          );
        }

        // No cache, fetch from API
        return freshData$;
      }),
      shareReplay(1)
    );

    // Clear cache if user logs out
    authState(this.auth).subscribe(firebaseUser => {
      if (!firebaseUser) {
        this.clearCache();
        this.clearMasqueradeState();
        this.stopInactivityTracking();
        this.closeInactivityDialog();
        this.inactivityLogoutInProgress = false;
        return;
      }

      this.startInactivityTracking();
    });
  }

  private async configureAuthPersistence(): Promise<void> {
    try {
      await setPersistence(this.auth, browserSessionPersistence);
    } catch {
      // Keep default persistence if browser blocks storage APIs.
    }
  }

  private loadCachedUser(): void {
    try {
      const cached = sessionStorage.getItem(this.cachedUserStorageKey);
      if (cached) {
        const parsed = JSON.parse(cached) as Partial<User> | null;
        if (!parsed || typeof parsed !== 'object') {
          return;
        }
        const user: User = {
          uid: parsed.uid || '',
          email: parsed.email ?? null,
          role: (parsed.role as 'admin' | 'user' | null) ?? null,
          twoFactorEnabled: parsed.twoFactorEnabled !== false,
          features: parsed.features && typeof parsed.features === 'object' ? parsed.features : {},
          isPlatformAdmin: parsed.isPlatformAdmin === true,
        };
        if (!user.uid) {
          return;
        }
        this.cachedUser$.next(user);
      }
    } catch (e) {
      // Ignore cache errors
    }
  }

  private cacheUser(user: User): void {
    try {
      sessionStorage.setItem(this.cachedUserStorageKey, JSON.stringify(user));
      this.cachedUser$.next(user);
      if (!this.canUseMasquerade(user)) {
        this.clearMasqueradeState();
      }
    } catch (e) {
      // Ignore cache errors
    }
  }

  private clearCache(): void {
    try {
      sessionStorage.removeItem(this.cachedUserStorageKey);
      localStorage.removeItem(this.activeOrgStorageKey);
      this.cachedUser$.next(null);
      this.clearMasqueradeState();
    } catch (e) {
      // Ignore cache errors
    }
  }

  private canUseMasquerade(user: User | null): boolean {
    const hasMasqueradeFeature = user?.features?.['masquerade_as'] === true;
    if (!user || user.role !== 'admin' || !hasMasqueradeFeature) {
      return false;
    }
    if (!user.isPlatformAdmin) {
      return true;
    }
    // Platform admins may masquerade only while explicitly in org mode.
    return this.getPreferredAppMode() === 'org';
  }

  private loadMasqueradeState(): void {
    try {
      const raw = localStorage.getItem(this.masqueradeStateStorageKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as Partial<MasqueradeState> | null;
      if (
        !parsed ||
        typeof parsed.userId !== 'string' ||
        !parsed.userId.trim() ||
        typeof parsed.displayName !== 'string'
      ) {
        return;
      }
      this.masqueradeState$.next({
        userId: parsed.userId.trim(),
        displayName: parsed.displayName.trim() || parsed.userId.trim(),
        email: typeof parsed.email === 'string' ? parsed.email : null,
      });
    } catch {
      // Ignore storage parse errors
    }
  }

  private persistMasqueradeState(state: MasqueradeState): void {
    try {
      localStorage.setItem(this.masqueradeStateStorageKey, JSON.stringify(state));
      this.masqueradeState$.next(state);
    } catch {
      // Ignore storage write errors
    }
  }

  private clearMasqueradeState(): void {
    try {
      localStorage.removeItem(this.masqueradeStateStorageKey);
      this.masqueradeState$.next(null);
    } catch {
      // Ignore storage access errors
    }
  }

  private startInactivityTracking(): void {
    if (!this.isTrackingActivity && typeof window !== 'undefined' && typeof document !== 'undefined') {
      this.inactivityEvents.forEach(eventName => {
        window.addEventListener(eventName, this.handleActivity, true);
      });
      window.addEventListener('focus', this.handleWindowFocus, true);
      document.addEventListener('visibilitychange', this.handleVisibilityChange, true);
      this.isTrackingActivity = true;
    }

    this.recordActivity();
    this.resetInactivityTimer();
  }

  private stopInactivityTracking(): void {
    if (this.isTrackingActivity && typeof window !== 'undefined' && typeof document !== 'undefined') {
      this.inactivityEvents.forEach(eventName => {
        window.removeEventListener(eventName, this.handleActivity, true);
      });
      window.removeEventListener('focus', this.handleWindowFocus, true);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange, true);
      this.isTrackingActivity = false;
    }

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private resetInactivityTimer(): void {
    if (!this.auth.currentUser || this.inactivityLogoutInProgress || this.inactivityDialogRef) {
      return;
    }

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    const elapsedMs = this.getElapsedInactivityMs();
    const hardTimeoutMs = environment.inactivityTimeoutMs;
    const warningWindowMs = this.inactivityWarningCountdownSeconds * 1000;
    const warningStartMs = Math.max(hardTimeoutMs - warningWindowMs, 0);

    if (elapsedMs >= hardTimeoutMs) {
      void this.logoutDueToInactivity();
      return;
    }

    if (elapsedMs >= warningStartMs) {
      const secondsUntilLogout = Math.max(1, Math.ceil((hardTimeoutMs - elapsedMs) / 1000));
      this.showInactivityWarningDialog(secondsUntilLogout);
      return;
    }

    this.inactivityTimer = setTimeout(() => {
      this.resetInactivityTimer();
    }, warningStartMs - elapsedMs);
  }

  private showInactivityWarningDialog(countdownSeconds: number): void {
    if (!this.auth.currentUser || this.inactivityLogoutInProgress || this.inactivityDialogRef) {
      return;
    }

    this.stopInactivityTracking();
    this.inactivityDialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      disableClose: true,
      width: '420px',
      data: {
        countdownSeconds,
        expiresAtMs: Date.now() + countdownSeconds * 1000,
      },
    });

    this.inactivityDialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
      this.inactivityDialogRef = null;
      if (this.ignoreNextInactivityDialogClose) {
        this.ignoreNextInactivityDialogClose = false;
        return;
      }

      if (result === 'stay') {
        this.recordActivity();
        this.startInactivityTracking();
        return;
      }

      void this.logoutDueToInactivity();
    });
  }

  private closeInactivityDialog(): void {
    if (this.inactivityDialogRef) {
      this.ignoreNextInactivityDialogClose = true;
      this.inactivityDialogRef.close();
      this.inactivityDialogRef = null;
    }
  }

  private async logoutDueToInactivity(): Promise<void> {
    if (this.inactivityLogoutInProgress) {
      return;
    }

    this.inactivityLogoutInProgress = true;
    this.closeInactivityDialog();
    this.stopInactivityTracking();
    this.clearCache();
    this.clearRecordedActivity();
    await this.revokeServerTwoFactorState();
    await signOut(this.auth);
    await this.router.navigate(['/login']);
    this.inactivityLogoutInProgress = false;
  }

  async handleSessionExpired(): Promise<void> {
    await this.handleExpiredSession(true);
  }

  private async handleExpiredSession(showTimeoutToast: boolean = false): Promise<void> {
    if (this.inactivityLogoutInProgress) {
      return;
    }

    this.inactivityLogoutInProgress = true;
    this.closeInactivityDialog();
    this.stopInactivityTracking();
    this.clearCache();
    this.clearRecordedActivity();
    await this.revokeServerTwoFactorState();
    try {
      await signOut(this.auth);
    } catch {
      // If Firebase has already torn down auth state, continue with local logout flow.
    }
    await this.router.navigate(['/login']);
    if (showTimeoutToast) {
      this.showSessionTimeoutToast();
    }
    this.inactivityLogoutInProgress = false;
  }

  private showSessionTimeoutToast(): void {
    if (this.sessionTimeoutToastVisible) {
      return;
    }
    this.sessionTimeoutToastVisible = true;
    const ref = this.snackBar.open('Your session timed out. Please sign in again', 'Close', {
      duration: 5000,
    });
    ref.afterDismissed().pipe(take(1)).subscribe(() => {
      this.sessionTimeoutToastVisible = false;
    });
  }

  private async revokeServerTwoFactorState(): Promise<void> {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        return;
      }
      const token = await currentUser.getIdToken();
      if (!token) {
        return;
      }
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/2fa/logout`, {}, { headers }));
    } catch {
      // Best-effort only; do not block local sign-out.
    }
  }

  private fetchUserDataObservable(firebaseUser: FirebaseUser): Observable<User | null> {
    return new Observable<User | null>(observer => {
      this.fetchUserDataFromApi(firebaseUser).then((user) => {
        if (!user) {
          observer.next(null);
          observer.complete();
          return;
        }
        observer.next(user);
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  private async fetchUserDataFromApi(firebaseUser: FirebaseUser): Promise<User | null> {
    const token = await firebaseUser.getIdToken();
    const baseHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const headers = await this.resolveHeadersWithActiveOrg(baseHeaders);
    try {
      const response = await firstValueFrom(
        this.http.get<{
          user: {
            userId: string;
            email: string | null;
            role: 'admin' | 'user' | null;
            enabled: boolean;
            twoFactorEnabled?: boolean;
            features?: Record<string, boolean>;
            isPlatformAdmin?: boolean;
          };
        }>(
          `${environment.apiUrl}/me`,
          { headers }
        )
      );

      const user = response?.user;
      if (!user || user.enabled === false) {
        return null;
      }
      return {
        uid: user.userId || firebaseUser.uid,
        email: user.email ?? firebaseUser.email,
        role: user.role ?? null,
        twoFactorEnabled: user.twoFactorEnabled !== false,
        features: user.features ?? {},
        isPlatformAdmin: user.isPlatformAdmin === true,
      };
    } catch (error: any) {
      throw error;
    }
  }

  private async resolveHeadersWithActiveOrg(headers: HttpHeaders): Promise<HttpHeaders> {
    const cachedOrgId = this.getStoredActiveOrgId();
    if (cachedOrgId) {
      return headers.set('X-Org-Id', cachedOrgId);
    }

    try {
      const response = await firstValueFrom(
        this.http.get<{
          activeOrgId: string | null;
          organizations: Array<{ orgId: string }>;
        }>(
          `${environment.apiUrl}/orgs`,
          { headers }
        )
      );
      const activeOrgId = response?.activeOrgId || response?.organizations?.[0]?.orgId || null;
      if (activeOrgId) {
        this.storeActiveOrgId(activeOrgId);
        return headers.set('X-Org-Id', activeOrgId);
      }
      return headers;
    } catch {
      return headers;
    }
  }

  private getStoredActiveOrgId(): string | null {
    try {
      const value = localStorage.getItem(this.activeOrgStorageKey);
      return value && value.trim() ? value.trim() : null;
    } catch {
      return null;
    }
  }

  private storeActiveOrgId(orgId: string): void {
    try {
      localStorage.setItem(this.activeOrgStorageKey, orgId);
    } catch {
      // Ignore storage access failures.
    }
  }

  async login(email: string, password: string): Promise<LoginResult> {
    try {
      await setPersistence(this.auth, browserSessionPersistence);
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      this.clearMasqueradeState();
      const profile = await this.fetchUserDataFromApi(credential.user);
      if (!profile) {
        await signOut(this.auth);
        this.clearCache();
        throw new Error('User account not set up');
      }

      // Cache user data immediately after login
      this.cacheUser(profile);
      // Always enter organization context after login; platform admins can switch explicitly.
      this.setPreferredAppMode('org');
      this.clearPendingTwoFactorChallenge();
      this.clearTwoFactorSessionToken();
      return {
        requiresTwoFactor: profile.twoFactorEnabled === true,
      };
    } catch (error: any) {
      const normalizedError = new Error(this.friendlyLoginErrorMessage(error)) as Error & { code?: string };
      normalizedError.code = typeof error?.code === 'string' ? error.code : 'AUTH_LOGIN_FAILED';
      throw normalizedError;
    }
  }

  private friendlyLoginErrorMessage(error: any): string {
    const code = typeof error?.code === 'string' ? error.code : '';
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Incorrect email or password.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many failed login attempts. Please wait a moment and try again.';
      case 'auth/network-request-failed':
        return 'Network error while signing in. Please check your connection and try again.';
      default:
        return 'Login failed. Please check your credentials and try again.';
    }
  }

  async logout(): Promise<void> {
    this.closeInactivityDialog();
    this.stopInactivityTracking();
    this.inactivityLogoutInProgress = false;
    this.clearCache();
    this.clearMasqueradeState();
    this.clearRecordedActivity();
    await this.revokeServerTwoFactorState();
    this.clearTwoFactorSessionToken();
    this.clearPendingTwoFactorChallenge();
    await signOut(this.auth);
    // Wait a brief moment to ensure auth state has updated
    await new Promise(resolve => setTimeout(resolve, 100));
    this.router.navigate(['/login']);
  }

  getCurrentUser(): Observable<User | null> {
    return this.user$;
  }

  getPreferredAppMode(): AppMode {
    try {
      const value = localStorage.getItem(this.appModeStorageKey);
      return value === 'platform' ? 'platform' : 'org';
    } catch {
      return 'org';
    }
  }

  setPreferredAppMode(mode: AppMode): void {
    try {
      localStorage.setItem(this.appModeStorageKey, mode);
    } catch {
      // Ignore storage access failures.
    }
  }

  resolveAppMode(user: User | null): AppMode {
    if (!user?.isPlatformAdmin) {
      return 'org';
    }
    return this.getPreferredAppMode();
  }

  getMasqueradeState(): Observable<MasqueradeState | null> {
    return this.masqueradeState$.asObservable();
  }

  startMasquerade(target: {
    userId: string;
    email?: string | null;
    name?: string | null;
    surname?: string | null;
  }): boolean {
    const actor = this.cachedUser$.value;
    if (!this.canUseMasquerade(actor)) {
      return false;
    }
    const targetUserId = String(target.userId || '').trim();
    if (!targetUserId || targetUserId === actor?.uid) {
      return false;
    }
    const displayName =
      `${String(target.name || '').trim()} ${String(target.surname || '').trim()}`.trim() ||
      String(target.email || '').trim() ||
      targetUserId;
    this.persistMasqueradeState({
      userId: targetUserId,
      displayName,
      email: typeof target.email === 'string' ? target.email : null,
    });
    return true;
  }

  stopMasquerade(): void {
    this.clearMasqueradeState();
  }

  getActAsUserId(): string | null {
    const actor = this.cachedUser$.value;
    if (!this.canUseMasquerade(actor)) {
      this.clearMasqueradeState();
      return null;
    }
    const state = this.masqueradeState$.value;
    if (!state?.userId || state.userId === actor?.uid) {
      return null;
    }
    return state.userId;
  }

  async getIdToken(): Promise<string | null> {
    let user = this.auth.currentUser;
    if (!user) {
      // On app boot we can have cached user UI state before Firebase Auth fully hydrates currentUser.
      user = await firstValueFrom(
        authState(this.auth).pipe(
          take(1),
          timeout(3000),
          catchError(() => of(null))
        )
      );
    }

    if (!user) {
      if (this.cachedUser$.value) {
        await this.handleExpiredSession(true);
      }
      return null;
    }

    try {
      return await user.getIdToken();
    } catch {
      await this.handleExpiredSession(true);
      return null;
    }
  }

  private recordActivity(): void {
    const timestamp = Date.now();
    try {
      localStorage.setItem(this.lastActivityStorageKey, String(timestamp));
    } catch {
      // Ignore storage access errors.
    }
  }

  private getElapsedInactivityMs(): number {
    const now = Date.now();
    try {
      const raw = localStorage.getItem(this.lastActivityStorageKey);
      const parsed = raw ? Number(raw) : Number.NaN;
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
      }
      return Math.max(0, now - parsed);
    } catch {
      // If storage is unavailable, fallback to timer behavior.
      return 0;
    }
  }

  private clearRecordedActivity(): void {
    try {
      localStorage.removeItem(this.lastActivityStorageKey);
    } catch {
      // Ignore storage access errors.
    }
  }

  setTwoFactorSessionToken(token: string): void {
    try {
      localStorage.setItem(this.twoFactorSessionStorageKey, token);
    } catch {
      // Ignore storage access errors.
    }
  }

  getTwoFactorSessionToken(): string | null {
    try {
      const value = localStorage.getItem(this.twoFactorSessionStorageKey);
      return value && value.trim() ? value.trim() : null;
    } catch {
      return null;
    }
  }

  clearTwoFactorSessionToken(): void {
    try {
      localStorage.removeItem(this.twoFactorSessionStorageKey);
    } catch {
      // Ignore storage access errors.
    }
  }

  setPendingTwoFactorChallenge(challenge: PendingTwoFactorChallenge): void {
    try {
      localStorage.setItem(this.pendingTwoFactorStorageKey, JSON.stringify(challenge));
    } catch {
      // Ignore storage access errors.
    }
  }

  getPendingTwoFactorChallenge(): PendingTwoFactorChallenge | null {
    try {
      const raw = localStorage.getItem(this.pendingTwoFactorStorageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<PendingTwoFactorChallenge>;
      if (
        !parsed ||
        typeof parsed.challengeId !== 'string' ||
        typeof parsed.expiresAt !== 'string' ||
        typeof parsed.resendAvailableAt !== 'string'
      ) {
        return null;
      }
      return {
        challengeId: parsed.challengeId,
        expiresAt: parsed.expiresAt,
        resendAvailableAt: parsed.resendAvailableAt,
      };
    } catch {
      return null;
    }
  }

  clearPendingTwoFactorChallenge(): void {
    try {
      localStorage.removeItem(this.pendingTwoFactorStorageKey);
    } catch {
      // Ignore storage access errors.
    }
  }
}
